/**
 * AddGroupMemberModal Component
 *
 * Modal for adding a member to a workspace group.
 *
 * SIMPLIFIED: Groups no longer manage permissions. They only provide:
 * - User organization within workspace
 * - Agent visibility scoping (agents with visibility_level='group')
 *
 * All permissions are managed at the workspace level (WorkspaceMember.permissions).
 */

import React, { useState, useMemo } from 'react';
import { Workspace, WorkspaceGroup, WorkspaceMember } from '../../../types/workspace';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { X, Search, Check, ChevronLeft, ChevronRight } from 'lucide-react';

interface AddGroupMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspace: Workspace;
  group: WorkspaceGroup;
  availableMembers: WorkspaceMember[];
  onSuccess: () => void;
}

export const AddGroupMemberModal: React.FC<AddGroupMemberModalProps> = ({
  isOpen,
  onClose,
  workspace,
  group,
  availableMembers,
  onSuccess,
}) => {
  const { addGroupMember } = useWorkspace();
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ITEMS_PER_PAGE = 20;

  // Filter members based on search query
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return availableMembers;

    const query = searchQuery.toLowerCase();
    return availableMembers.filter((member) => {
      const name = (member.user_name || member.user?.name || '').toLowerCase();
      const email = (member.user_email || member.user?.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [availableMembers, searchQuery]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredMembers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  // Reset to page 1 when search query changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Toggle user selection
  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select/deselect all on current page
  const toggleSelectAllOnPage = () => {
    const pageIds = paginatedMembers.map(m => m.user_id);
    const allPageSelected = pageIds.every(id => selectedUserIds.includes(id));

    if (allPageSelected) {
      // Deselect all on page
      setSelectedUserIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      // Select all on page
      setSelectedUserIds(prev => {
        const newIds = [...prev];
        pageIds.forEach(id => {
          if (!newIds.includes(id)) newIds.push(id);
        });
        return newIds;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (selectedUserIds.length === 0) {
      setError('Por favor selecione pelo menos um usuário');
      return;
    }

    setIsSubmitting(true);

    try {
      // Add each selected user to the group
      for (const userId of selectedUserIds) {
        await addGroupMember(workspace.id, group.id, {
          user_id: userId,
        });
      }

      // Reset form
      setSelectedUserIds([]);
      setSearchQuery('');
      setCurrentPage(1);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao adicionar membros');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedUserIds([]);
      setSearchQuery('');
      setCurrentPage(1);
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 w-screen h-screen bg-black/60 z-[60]"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl shadow-2xl z-[70] max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#333333] sticky top-0 bg-[#2a2a2a] z-10">
          <div>
            <h2 className="text-xl font-semibold text-white">Adicionar Membro ao Grupo</h2>
            <p className="text-sm text-[#888888] mt-1">Adicionar um membro a {group.name}</p>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Search Input */}
          <div>
            <label htmlFor="search-input" className="block text-sm font-medium text-[#cccccc] mb-2">
              Buscar Usuários
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" />
              <input
                id="search-input"
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isSubmitting || availableMembers.length === 0}
                className="
                  w-full pl-10 pr-4 py-3
                  bg-[#1a1a1a] border border-[#333333]
                  text-white placeholder-[#666666]
                  rounded-lg
                  focus:outline-none focus:border-[#F4CC04]
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              />
            </div>
          </div>

          {/* User Selection List */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[#cccccc]">
                Selecionar Usuários * ({selectedUserIds.length} selecionado{selectedUserIds.length !== 1 ? 's' : ''})
              </label>
              {paginatedMembers.length > 0 && (
                <button
                  type="button"
                  onClick={toggleSelectAllOnPage}
                  disabled={isSubmitting}
                  className="text-xs text-[#F4CC04] hover:text-[#D4B104] transition-colors disabled:opacity-50"
                >
                  {paginatedMembers.every(m => selectedUserIds.includes(m.user_id))
                    ? 'Desmarcar página'
                    : 'Selecionar página'
                  }
                </button>
              )}
            </div>

            <div className="
              bg-[#1a1a1a] border border-[#333333]
              rounded-lg
            ">
              {availableMembers.length === 0 ? (
                <div className="p-4 text-center text-[#888888] text-sm">
                  Nenhum membro disponível para adicionar
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className="p-4 text-center text-[#888888] text-sm">
                  Nenhum usuário encontrado para "{searchQuery}"
                </div>
              ) : (
                <>
                  <div className="max-h-96 overflow-y-auto divide-y divide-[#333333]">
                    {paginatedMembers.map((member) => (
                      <label
                        key={member.user_id}
                        className="
                          flex items-center gap-3 p-3
                          hover:bg-[#2a2a2a] cursor-pointer
                          transition-colors
                        "
                      >
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(member.user_id)}
                          onChange={() => toggleUserSelection(member.user_id)}
                          disabled={isSubmitting}
                          className="
                            w-5 h-5
                            rounded border-[#333333]
                            bg-[#1a1a1a]
                            text-[#F4CC04]
                            focus:ring-2 focus:ring-[#F4CC04] focus:ring-offset-0
                            disabled:opacity-50 disabled:cursor-not-allowed
                          "
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-white truncate">
                              {member.user_name || member.user?.name || `User ${member.user_id}`}
                            </p>
                            {selectedUserIds.includes(member.user_id) && (
                              <Check className="w-4 h-4 text-[#F4CC04] flex-shrink-0" />
                            )}
                          </div>
                          {(member.user_email || member.user?.email) && (
                            <p className="text-xs text-[#888888] truncate">
                              {member.user_email || member.user?.email}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between p-3 border-t border-[#333333] bg-[#1a1a1a]">
                      <p className="text-xs text-[#888888]">
                        Mostrando {startIndex + 1}-{Math.min(endIndex, filteredMembers.length)} de {filteredMembers.length}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1 || isSubmitting}
                          className="
                            p-1.5
                            text-[#888888] hover:text-white
                            hover:bg-[#333333]
                            rounded
                            transition-colors
                            disabled:opacity-30 disabled:cursor-not-allowed
                          "
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-[#cccccc] px-2">
                          Página {currentPage} de {totalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages || isSubmitting}
                          className="
                            p-1.5
                            text-[#888888] hover:text-white
                            hover:bg-[#333333]
                            rounded
                            transition-colors
                            disabled:opacity-30 disabled:cursor-not-allowed
                          "
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <p className="text-xs text-[#888888] mt-2">
              Apenas membros do workspace que não estão neste grupo são mostrados
            </p>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-400">
              <strong>Nota:</strong> Grupos são apenas para organização de usuários e controle de visibilidade de agentes.
              Todas as permissões funcionais são gerenciadas no nível do workspace.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="
                px-4 py-2
                text-[#888888] hover:text-white
                hover:bg-[#333333]
                rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || selectedUserIds.length === 0}
              className="
                px-4 py-2
                bg-[#F4CC04] hover:bg-[#D4B104]
                text-black font-medium
                rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center gap-2
              "
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                  <span>Adicionando...</span>
                </>
              ) : (
                <span>
                  Adicionar {selectedUserIds.length > 0 ? `(${selectedUserIds.length})` : 'Membros'}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddGroupMemberModal;
